import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { PRIMARY_COLORS, WHITE } from '../../common/colors';
import { ladderFor } from '../../common/ctaLadder';
import { appLink } from '../../common/appLinks';
import { startProCheckout, startSignup } from '../../common/auth';
import useSignedIn from '../../common/useSignedIn';

// The CTA ladder (ocl_online#170): the row above the footer on every page.
// The columns keep one order everywhere; the page only picks which one gets
// the filled button (src/common/ctaLadder.js). Spec:
// docs/superpowers/specs/2026-09-27-cta-ladder-design.md
//
// /newsletter is an nginx redirect to Brevo, not an app route, so it's a
// plain href (a real page load). The call is an app route. Pro goes the way
// of every Pro button (startProCheckout), with /pricing as its plain link.
const COLUMNS = {
  signup: { icon: PersonAddAltOutlinedIcon, copy: 'cta.signup', link: () => ({ href: '#', onClick: startSignup }) },
  openApp: { icon: LoginOutlinedIcon, copy: 'cta.open_app', link: ({ termBrowserURL }) => ({ href: termBrowserURL }) },
  newsletter: { icon: EmailOutlinedIcon, copy: 'cta.newsletter', link: () => ({ href: '/newsletter' }) },
  call: { icon: GroupsOutlinedIcon, copy: 'cta.call', link: () => ({ component: RouterLink, to: '/#community-calls' }) },
  pro: { icon: WorkspacePremiumOutlinedIcon, copy: 'cta.pro', link: () => ({ href: '/pricing', onClick: startProCheckout }) },
};

// The filled button: white on the dark band, like the /pricing hero's.
const FILLED_SX = {
  backgroundColor: WHITE, color: 'primary.main', whiteSpace: 'nowrap', px: 3,
  '&:hover': { backgroundColor: PRIMARY_COLORS['90'] },
};

// The others: white text links with an arrow, as the row above the footer's were.
const LINK_SX = {
  color: WHITE, textTransform: 'none', fontWeight: 600, px: 1, mx: -1,
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
};

const Column = ({ id, icon: Icon, title, text, button, link, filled }) => (
  <Box data-cta={id} sx={{ display: 'flex', flexDirection: 'column' }}>
    <Icon sx={{ fontSize: 40, color: PRIMARY_COLORS['90'], mb: 2 }} />
    <Typography variant="h5" component="h3" sx={{ color: WHITE, mb: 1 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: PRIMARY_COLORS['80'], mb: 2.5 }}>
      {text}
    </Typography>
    <Box sx={{ mt: 'auto' }}>
      {filled ? (
        <Button {...link} data-cta-filled="true" sx={FILLED_SX}>{button}</Button>
      ) : (
        <Button {...link} endIcon={<ArrowForwardIosIcon sx={{ fontSize: '12px !important' }} />} sx={LINK_SX}>{button}</Button>
      )}
    </Box>
  </Box>
);

const CtaLadder = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const signedIn = useSignedIn();
  // appLink reads window.location, so it's only called once `signedIn` is
  // true, which happens after mount (docs/SEO-AUDIT.md bug #8).
  const termBrowserURL = signedIn ? appLink('termBrowser') : undefined;
  const { columns, primary } = ladderFor({ pathname, signedIn });

  return (
    <Box component="section" aria-labelledby="cta-ladder-heading" sx={{ backgroundColor: PRIMARY_COLORS['10'] }}>
      <Typography id="cta-ladder-heading" component="h2" sx={visuallyHidden}>
        {t('cta.heading')}
      </Typography>
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${columns.length}, 1fr)` }, gap: { xs: 5, md: columns.length > 3 ? 5 : 8 } }}>
          {columns.map(id => {
            const { icon, copy, link } = COLUMNS[id];
            return (
              <Column
                key={id}
                id={id}
                icon={icon}
                title={t(`${copy}.title`)}
                text={t(`${copy}.text`)}
                button={t(`${copy}.button`)}
                link={link({ termBrowserURL })}
                filled={id === primary}
              />
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default CtaLadder;
